/**
 * React Query hooks for Calendar operations.
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useAuth } from '@aaas/shared';
import calendarService from '../services/calendar.service';
import type { CalendarEventCreateInput, CalendarEventUpdateInput } from '../types';

interface UseCalendarEventsQueryParams {
  workspaceId: string | undefined;
  idToken: string | undefined;
  startDate?: string;
  endDate?: string;
  memberId?: string;
  cvId?: string;
  enabled?: boolean;
}

export const useCalendarEventsQuery = ({
  workspaceId,
  idToken,
  startDate,
  endDate,
  memberId,
  cvId,
  enabled = true,
}: UseCalendarEventsQueryParams) => {
  return useQuery({
    queryKey: [
      'calendarEvents',
      workspaceId,
      startDate ?? '',
      endDate ?? '',
      memberId ?? '',
      cvId ?? '',
    ],
    queryFn: async () => {
      if (!workspaceId || !idToken) {
        throw new Error('Workspace ID and token are required');
      }
      return await calendarService.listEvents(workspaceId, idToken, {
        startDate,
        endDate,
        memberId,
        cvId,
        limit: 100,
        sortBy: 'start_time',
        order: 'asc',
      });
    },
    enabled: enabled && !!workspaceId && !!idToken,
    placeholderData: keepPreviousData,
    staleTime: 30000,
    gcTime: 10 * 60 * 1000,
  });
};

interface UseCalendarEventDetailQueryParams {
  eventId: string | undefined;
  workspaceId: string | undefined;
  idToken: string | undefined;
  enabled?: boolean;
}

export const useCalendarEventDetailQuery = ({
  eventId,
  workspaceId,
  idToken,
  enabled = true,
}: UseCalendarEventDetailQueryParams) => {
  return useQuery({
    queryKey: ['calendarEvent', eventId],
    queryFn: async () => {
      if (!eventId || !workspaceId || !idToken) {
        throw new Error('Event ID, Workspace ID and token are required');
      }
      return await calendarService.getEvent(eventId, workspaceId, idToken);
    },
    enabled: enabled && !!eventId && !!workspaceId && !!idToken,
    staleTime: 30000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useCreateCalendarEventMutation = () => {
  const queryClient = useQueryClient();
  const { currentWorkspace, idToken } = useAuth();

  return useMutation({
    mutationFn: async (data: CalendarEventCreateInput) => {
      if (!currentWorkspace?.id || !idToken) {
        throw new Error('Workspace and token are required');
      }
      return await calendarService.createEvent(currentWorkspace.id, idToken, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendarEvents'] });
    },
  });
};

export const useUpdateCalendarEventMutation = () => {
  const queryClient = useQueryClient();
  const { currentWorkspace, idToken } = useAuth();

  return useMutation({
    mutationFn: async ({ eventId, data }: { eventId: string; data: CalendarEventUpdateInput }) => {
      if (!currentWorkspace?.id || !idToken) {
        throw new Error('Workspace and token are required');
      }
      return await calendarService.updateEvent(eventId, currentWorkspace.id, idToken, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendarEvents'] });
      queryClient.invalidateQueries({ queryKey: ['calendarEvent'] });
    },
  });
};

export const useDeleteCalendarEventMutation = () => {
  const queryClient = useQueryClient();
  const { currentWorkspace, idToken } = useAuth();

  return useMutation({
    mutationFn: async (eventId: string) => {
      if (!currentWorkspace?.id || !idToken) {
        throw new Error('Workspace and token are required');
      }
      await calendarService.deleteEvent(eventId, currentWorkspace.id, idToken);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendarEvents'] });
    },
  });
};
